import { useCallback, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/client'
import type { Incident, Page, Severity } from '../api/types'
import { Card, Empty, ErrorNote, Labels, SeverityBadge, Spinner, Stat, StatusBadge, TableScroll, humanDuration, since } from '../components/ui'
import { useAction, useQuery } from '../hooks/useApi'
import { useAuth } from '../auth/AuthContext'
import { useIncidentStream } from '../hooks/useIncidentStream'

const SEVERITIES: Severity[] = ['critical', 'high', 'warning', 'info']

export function DashboardPage() {
  const { me } = useAuth()
  const action = useAction()
  const [linkCode, setLinkCode] = useState<{ code: string; instructions: string } | null>(null)

  const requestLinkCode = async () => {
    await action.run(async () => {
      setLinkCode(
        await api.post<{ code: string; instructions: string }>('/auth/telegram/link-code'),
      )
    })
  }

  const open = useQuery(
    () =>
      api.get<Page<Incident>>('/incidents', {
        status: ['firing', 'escalated', 'acknowledged', 'silenced'],
        limit: 100,
      }),
    [],
  )
  const resolved = useQuery(
    () => api.get<Page<Incident>>('/incidents', { status: ['resolved'], limit: 100 }),
    [],
  )

  // Любое изменение инцидента приходит по WebSocket — перечитываем срез.
  useIncidentStream(
    useCallback(() => {
      open.reload()
      resolved.reload()
    }, [open.reload, resolved.reload]),
  )

  const incidents = open.data?.items ?? []

  const bySeverity = useMemo(() => {
    const counts: Record<Severity, number> = { critical: 0, high: 0, warning: 0, info: 0 }
    incidents
      .filter((incident) => incident.status !== 'resolved')
      .forEach((incident) => {
        counts[incident.severity] += 1
      })
    return counts
  }, [incidents])

  const unacknowledged = incidents.filter(
    (incident) => incident.status === 'firing' || incident.status === 'escalated',
  )

  // Медиана устойчивее среднего: один инцидент, забытый на выходных,
  // не должен превращать метрику в бессмысленное число.
  const medianAck = useMemo(() => median(resolved.data?.items ?? [], (i) => i.time_to_ack_seconds), [
    resolved.data,
  ])
  const medianResolve = useMemo(
    () => median(resolved.data?.items ?? [], (i) => i.time_to_resolve_seconds),
    [resolved.data],
  )

  if (open.loading && !open.data) return <Spinner />

  return (
    <>
      <ErrorNote>{open.error}</ErrorNote>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Stat
          label="Требуют реакции"
          value={unacknowledged.length}
          tone={unacknowledged.length ? 'critical' : 'good'}
        />
        {SEVERITIES.map((severity) => (
          <Stat key={severity} label={severity} value={bySeverity[severity]} />
        ))}
      </div>

      {me && !me.telegram_linked && (
        <Card
          title="Telegram не привязан"
          actions={
            <button className="btn-primary" disabled={action.pending} onClick={requestLinkCode}>
              Получить код
            </button>
          }
        >
          <div className="p-4 space-y-2 text-sm text-slate-400">
            <p>
              Пока Telegram не связан с учётной записью, кнопки под сообщениями бота работать
              не будут: платформа не сможет понять, кто нажал.
            </p>
            {linkCode && (
              <div className="rounded-md border border-sky-500/40 bg-sky-500/10 p-3 text-slate-200">
                <div>
                  Отправьте боту в личных сообщениях:{' '}
                  <code className="font-mono">/link {linkCode.code}</code>
                </div>
                <div className="text-xs text-slate-400 mt-1">Код действует 10 минут.</div>
              </div>
            )}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Медиана до ack" value={humanDuration(medianAck)} />
        <Stat label="Медиана до закрытия" value={humanDuration(medianResolve)} />
        <Stat label="Открытых всего" value={incidents.length} />
        <Stat label="Закрыто (последние 100)" value={resolved.data?.items.length ?? 0} />
      </div>

      <Card
        title="Открытые инциденты"
        actions={
          <Link className="btn-ghost" to="/incidents">
            Все инциденты
          </Link>
        }
      >
        {incidents.length === 0 ? (
          <Empty>Открытых инцидентов нет.</Empty>
        ) : (
          <TableScroll minWidth={720}>
            <thead>
              <tr>
                <th className="th">Severity</th>
                <th className="th">Инцидент</th>
                <th className="th">Статус</th>
                <th className="th">Метки</th>
                <th className="th">Возраст</th>
                <th className="th">Алертов</th>
              </tr>
            </thead>
            <tbody>
              {[...incidents]
                .sort(
                  (a, b) =>
                    severityRank(a.severity) - severityRank(b.severity) ||
                    new Date(a.started_at).getTime() - new Date(b.started_at).getTime(),
                )
                .slice(0, 15)
                .map((incident) => (
                  <tr key={incident.id} className="hover:bg-ink-700/40">
                    <td className="td">
                      <SeverityBadge severity={incident.severity} />
                    </td>
                    <td className="td">
                      <Link className="text-sky-300 hover:underline" to={`/incidents/${incident.id}`}>
                        {incident.title}
                      </Link>
                    </td>
                    <td className="td">
                      <StatusBadge status={incident.status} />
                    </td>
                    <td className="td">
                      <Labels labels={incident.labels} limit={4} />
                    </td>
                    <td className="td tabular-nums text-slate-400">{since(incident.started_at)}</td>
                    <td className="td tabular-nums text-slate-400">{incident.alerts_count}</td>
                  </tr>
                ))}
            </tbody>
          </TableScroll>
        )}
      </Card>
    </>
  )
}

function severityRank(severity: Severity): number {
  return { critical: 0, high: 1, warning: 2, info: 3 }[severity]
}

function median(items: Incident[], pick: (i: Incident) => number | null): number | null {
  const values = items
    .map(pick)
    .filter((value): value is number => value !== null && Number.isFinite(value))
    .sort((a, b) => a - b)
  if (!values.length) return null
  const middle = Math.floor(values.length / 2)
  return values.length % 2 ? values[middle] : (values[middle - 1] + values[middle]) / 2
}
